import React, { useState, useEffect } from 'react';
import { Modal, Tabs, List, Button, Form, Input, Select, DatePicker, message, Popconfirm, Tag, Space, Empty, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

const SkillsManagementModal = ({ visible, onClose, userId, managerId = 1 }) => {
  const [skills, setSkills] = useState({ technical: [], artificial: [], ai: [] });
  const [loading, setLoading] = useState(false);
  const [addFormVisible, setAddFormVisible] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible && userId) {
      fetchSkills();
    }
  }, [visible, userId]);

  const fetchSkills = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`https://api.eilandigital.com/eilanApplication-0.0.1-SNAPSHOT/api/user-skills/user/${userId}`);
      setSkills(response.data.skills || { technical: [], artificial: [], ai: [] });
    } catch (error) {
      console.error('Error fetching skills:', error);
      message.error('Failed to load skills');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingSkill(null);
    form.resetFields();
    form.setFieldsValue({ proficiencyLevel: 'intermediate' });
    setAddFormVisible(true);
  };

  const handleEdit = (skill) => {
    setEditingSkill(skill);
    form.setFieldsValue({
      skillName: skill.skillName,
      skillCategory: skill.category,
      proficiencyLevel: skill.proficiency,
      acquiredDate: skill.acquiredDate ? dayjs(skill.acquiredDate) : null,
      notes: skill.notes
    });
    setAddFormVisible(true);
  };

  const handleDelete = async (skillId) => {
    try {
      await axios.delete(`https://api.eilandigital.com/eilanApplication-0.0.1-SNAPSHOT/api/user-skills/${skillId}`);
      message.success('Skill deleted successfully');
      fetchSkills();
    } catch (error) {
      console.error('Error deleting skill:', error);
      message.error('Failed to delete skill');
    }
  };

  const handleSaveSkill = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        acquiredDate: values.acquiredDate ? values.acquiredDate.format('YYYY-MM-DD') : null,
        userId: userId,
        addedBy: managerId
      };

      if (editingSkill) {
        await axios.put(`https://api.eilandigital.com/eilanApplication-0.0.1-SNAPSHOT/api/user-skills/${editingSkill.skillId}`, payload);
        message.success('Skill updated successfully');
      } else {
        await axios.post('https://api.eilandigital.com/eilanApplication-0.0.1-SNAPSHOT/api/user-skills', payload);
        message.success('Skill added successfully');
      }

      setAddFormVisible(false);
      form.resetFields();
      fetchSkills();
    } catch (error) {
      console.error('Error saving skill:', error);
      message.error('Failed to save skill');
    }
  };

  const getProficiencyColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'expert': return 'red';
      case 'advanced': return 'orange';
      case 'intermediate': return 'blue';
      case 'beginner': return 'green';
      default: return 'default';
    }
  };

  const renderSkillList = (categorySkills) => {
    if (!categorySkills || categorySkills.length === 0) {
      return <Empty description="No skills in this category" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    return (
      <List
        dataSource={categorySkills}
        renderItem={(skill) => (
          <List.Item
            actions={[
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(skill)}>Edit</Button>,
              <Popconfirm
                title="Delete this skill?"
                description="This action cannot be undone."
                onConfirm={() => handleDelete(skill.skillId)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{ danger: true }}
              >
                <Button type="link" size="small" danger icon={<DeleteOutlined />}>Delete</Button>
              </Popconfirm>
            ]}
          >
            <List.Item.Meta
              title={
                <Space>
                  <span style={{ fontWeight: 500 }}>{skill.skillName}</span>
                  <Tag color={getProficiencyColor(skill.proficiency)}>
                    {skill.proficiency?.toUpperCase() || 'N/A'}
                  </Tag>
                </Space>
              }
              description={
                <div>
                  {skill.acquiredDate && (
                    <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      Acquired: {dayjs(skill.acquiredDate).format('MMM YYYY')}
                    </span>
                  )}
                  {skill.notes && (
                    <div style={{ marginTop: 4, fontSize: '12px', color: '#595959' }}>
                      {skill.notes}
                    </div>
                  )}
                </div>
              }
            />
          </List.Item>
        )}
      />
    );
  };

  return (
    <>
      <Modal
        title={<div style={{ fontSize: '18px', fontWeight: 600 }}>Manage Skills</div>}
        open={visible}
        onCancel={onClose}
        width={700}
        footer={[<Button key="close" onClick={onClose}>Close</Button>]}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" tip="Loading skills..." />
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew} block>
                Add New Skill
              </Button>
            </div>

            <Tabs defaultActiveKey="technical">
              <TabPane tab={`Technical (${skills.technical?.length || 0})`} key="technical">
                {renderSkillList(skills.technical)}
              </TabPane>
              <TabPane tab={`Artificial (${skills.artificial?.length || 0})`} key="artificial">
                {renderSkillList(skills.artificial)}
              </TabPane>
              <TabPane tab={`AI (${skills.ai?.length || 0})`} key="ai">
                {renderSkillList(skills.ai)}
              </TabPane>
            </Tabs>
          </>
        )}
      </Modal>

      <Modal
        title={editingSkill ? 'Edit Skill' : 'Add New Skill'}
        open={addFormVisible}
        onOk={handleSaveSkill}
        onCancel={() => {
          setAddFormVisible(false);
          form.resetFields();
        }}
        okText={editingSkill ? 'Update' : 'Add'}
        okButtonProps={{ icon: <SaveOutlined /> }}
        cancelButtonProps={{ icon: <CloseOutlined /> }}
        width={500}
      >
        <Form form={form} layout="vertical" initialValues={{ proficiencyLevel: 'intermediate' }}>
          <Form.Item label="Skill Name" name="skillName" rules={[{ required: true, message: 'Please enter skill name' }]}>
            <Input placeholder="e.g., React Development" />
          </Form.Item>

          <Form.Item label="Category" name="skillCategory" rules={[{ required: true, message: 'Please select category' }]}>
            <Select placeholder="Select category">
              <Option value="technical">Technical</Option>
              <Option value="artificial">Artificial</Option>
              <Option value="ai">AI</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Proficiency Level" name="proficiencyLevel">
            <Select>
              <Option value="beginner">Beginner</Option>
              <Option value="intermediate">Intermediate</Option>
              <Option value="advanced">Advanced</Option>
              <Option value="expert">Expert</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Acquired Date (Month learned)" name="acquiredDate" tooltip="Select the month and year when this skill was learned">
            <DatePicker style={{ width: '100%' }} format="MMM YYYY" picker="month" />
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <TextArea rows={2} placeholder="Additional notes about this skill" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default SkillsManagementModal;